<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="fas fa-broadcast-tower" class="q-mb-sm" /> {{ $t(title_webcasts) }}
		</div>

		<filters
			class="filter-card"
			:options="getOptionsFilter"
			@requestFiltered="requestFiltered"
			@clearAndRequest="clearAndRequest"
		/>

		<q-table
			:data="webcasts"
			:columns="columns"
			:pagination.sync="server_pagination"
			@request="request"
			:loading="loading"
			:filter="filter"
			row-key="name"
			:no-data-label="$t('no_data_avaliable')"
			:no-results-label="$t('no_results_found')"
			:loading-label="$t('loading_data')"
			:rows-per-page-label="$t('items_per_page')"
			:rows-per-page-options="[5, 10, 50, 100]"
			binary-state-sort
		>
			<template slot="top" slot-scope="props" :props="props">
				<q-search
					v-model="filter"
					:debounce="600"
					icon="fas fa-search"
					:placeholder="$t('search')"
				/>

				<div class="col" />

				<!-- <q-btn-dropdown color="secondary" :label="$t('reports_bi')" class="q-mr-sm" ref="dropdownReport" v-if="report_list.length > 0 && permissions.reports">
					<q-list highlight separator link>
						<q-item
							v-for="(report, index) in report_list"
							:key="`i${index}`"
							@click.native="showReport(report)">
							<q-item-main>
								{{ $t(report.slug) }}
							</q-item-main>
						</q-item>
					</q-list>
				</q-btn-dropdown> -->

				<q-btn
					color="secondary"
					v-if="permissions.reports"
					class="q-mr-sm"
					icon="fas fa-download"
					:disabled="$buttonDelay('webcasts_report_button_delay')"
					:label="$t('webcasts_report')"
					@click="downloadReport()"
				>
					<q-tooltip
						v-if="$buttonDelay('webcasts_report_button_delay')"
						anchor="top middle"
						self="bottom middle"
						:offset="[10, 10]"
					>
						{{ $t('report_button_delay') }}
					</q-tooltip>
				</q-btn>
				<q-btn
					color="secondary"
					icon="fas fa-plus"
					:label="$t('new')"
					@click="$router.push({ name: 'admin_webcast', params: { id: 0 } })"
					:disable="!permissions.webcasts_manage"
				/>
			</template>

			<q-tr slot="header" slot-scope="props" :props="props">
				<q-th v-for="col in props.cols" :key="col.name" :props="props" v-show="col.show">
					{{ $t(col.label) }}
				</q-th>
			</q-tr>

			<q-tr slot="body" slot-scope="props" :props="props">
				<q-td auto-width key="id" :props="props" v-if="showColumn(props.colsMap, 'id')">
					{{ props.row.id }}
				</q-td>

				<q-td key="title" :props="props" v-if="showColumn(props.colsMap, 'title')">
					{{ props.row.title }}
				</q-td>

				<q-td key="code" :props="props" v-show="showColumn(props.colsMap, 'code')">
					{{ props.row.code }}
				</q-td>

				<q-td key="provider" :props="props" v-if="showColumn(props.colsMap, 'provider')">
					{{ getProvider(props.row.provider) }}
				</q-td>

				<q-td
					key="permission"
					:props="props"
					v-if="showColumn(props.colsMap, 'permission')"
				>
					<q-btn
						color="primary"
						text-color="white"
						:label="getQuantity(props.row.permission)"
						no-ripple
					>
						<q-tooltip>
							<p v-if="props.row.permission.areas.length > 0" class="tooltip q-mb-sm">
								{{ $t('areas') }}:
								<span
									v-for="(area, index) in props.row.permission.areas"
									:key="index"
								>
									{{ getArea(area) }}
									<span v-if="props.row.permission.areas.length - 1 > index"
										>,
									</span>
								</span>
							</p>
							<p
								v-if="props.row.permission.user_groups.length > 0"
								class="tooltip q-mb-sm"
							>
								{{ $t('user_groups') }}:
								<span
									v-for="(user_group, index) in props.row.permission.user_groups"
									:key="index"
								>
									{{ getUserGroup(user_group) }}
									<span v-if="props.row.permission.user_groups.length - 1 > index"
										>,
									</span>
								</span>
							</p>
							<p
								v-if="props.row.permission.profiles.length > 0"
								class="tooltip q-mb-sm"
							>
								{{ $t('profiles') }}:
								<span
									v-for="(profile, index) in props.row.permission.profiles"
									:key="index"
								>
									{{ getUserProfile(profile) }}
									<span v-if="props.row.permission.profiles.length - 1 > index"
										>,
									</span>
								</span>
							</p>
						</q-tooltip>
					</q-btn>
				</q-td>

				<q-td key="tags" :props="props" v-if="showColumn(props.colsMap, 'tags')">
					<q-btn
						color="primary"
						text-color="white"
						:label="getQuantityTags(props.row.tags)"
						no-ripple
					>
						<q-tooltip v-if="props.row.tags.length > 0">
							<p class="tooltip q-mb-sm">
								{{ $t('tags') }}:
								<span v-for="(tag, index) in props.row.tags" :key="index">
									{{ getTag(tag) }}
									<span v-if="props.row.tags.length - 1 > index">, </span>
								</span>
							</p>
						</q-tooltip>
					</q-btn>
				</q-td>

				<q-td
					key="total_score"
					:props="props"
					v-if="showColumn(props.colsMap, 'total_score')"
				>
					{{ props.row.total_score }}
				</q-td>

				<q-td key="use_chat" :props="props" v-if="showColumn(props.colsMap, 'use_chat')">
					{{ props.row.use_chat ? $t('yes') : $t('no') }}
				</q-td>

				<q-td
					key="chat_moderated"
					:props="props"
					v-if="showColumn(props.colsMap, 'chat_moderated')"
				>
					{{ props.row.chat_moderated ? $t('yes') : $t('no') }}
				</q-td>

				<q-td
					key="transmission_date"
					:props="props"
					v-if="showColumn(props.colsMap, 'transmission_date')"
				>
					{{ props.row.transmission_date }}
				</q-td>

				<q-td key="timezone" :props="props" v-if="showColumn(props.colsMap, 'timezone')">
					{{ props.row.timezone }}
				</q-td>

				<q-td
					key="created_date"
					:props="props"
					v-show="showColumn(props.colsMap, 'created_date')"
				>
					{{ props.row.created_date }}
				</q-td>

				<q-td
					key="updated_date"
					:props="props"
					v-show="showColumn(props.colsMap, 'updated_date')"
				>
					{{ props.row.updated_date }}
				</q-td>

				<q-td
					key="unpublish_date"
					:props="props"
					v-show="showColumn(props.colsMap, 'unpublish_date')"
				>
					{{ props.row.unpublish_date }}
				</q-td>

				<q-td
					key="status"
					:props="props"
					auto-width
					v-if="showColumn(props.colsMap, 'status')"
				>
					<q-chip
						small
						:icon="getIconStatus(props.row.status)"
						:color="getColorStatus(props.row.status)"
						class="full-width"
					>
						{{ $t(getStatus(props.row.status)) }}
					</q-chip>
				</q-td>

				<q-td key="actions" :props="props" v-if="showColumn(props.colsMap, 'actions')">
					<q-btn
						flat
						round
						color="primary"
						icon="fas fa-pencil-alt"
						@click="
							$router.push({
								name: 'admin_webcast',
								params: { id: props.row.actions },
							})
						"
					>
						<q-tooltip>
							{{ $t('view_or_edit_tooltip') }}
						</q-tooltip>
					</q-btn>

					<q-btn
						flat
						round
						color="secondary"
						icon="fas fa-chart-line"
						@click="showWebcastReport(props.row)"
					>
						<q-tooltip>
							{{ $t('view_report_for_this_webcast_tooltip') }}
						</q-tooltip>
					</q-btn>

					<q-btn
						flat
						round
						color="negative"
						icon="far fa-trash-alt"
						@click="confirmRemove(props.row.actions)"
						:disable="!permissions.webcasts_manage"
					>
						<q-tooltip>
							{{ $t('delete_tooltip') }}
						</q-tooltip>
					</q-btn>
				</q-td>
			</q-tr>

			<q-tr slot="bottom-row" slot-scope="props" :props="props">
				<q-td colspan="100%">
					<q-btn
						flat
						round
						dense
						color="secondary"
						size="small"
						icon="fas fa-cog"
						@click="show_customize_columns = true"
					>
						<q-tooltip>
							{{ $t('configure_table_tooltip') }}
						</q-tooltip>
					</q-btn>
				</q-td>
			</q-tr>
		</q-table>

		<q-dialog v-model="confirm_remove" @ok="removeItem">
			<span slot="title">{{ $t('remove_item') }}</span>

			<span slot="message">{{ $t('confirm_remove_item') }}</span>

			<template slot="buttons" slot-scope="props" :props="props">
				<q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
				<q-btn color="negative" :label="$t('remove')" @click="removeItem()" />
			</template>
		</q-dialog>

		<q-modal
			v-model="show_customize_columns"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="saveColumns()" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('personalize_columns') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row gutter-md">
						<div
							class="col-xs-12 col-sm-4"
							v-for="(column, index) in columns"
							:key="index"
							v-if="column.label && column.label != '#'"
						>
							<q-checkbox v-model="column.show" :label="$t(column.label)" />
						</div>
					</div>

					<div class="row reverse gutter-md q-mt-md">
						<div class="col-xs-12 col-sm-3">
							<q-btn
								icon="fas fa-times"
								color="primary"
								class="fit"
								:label="$t('close')"
								@click="saveColumns()"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-modal
			v-model="show_report"
			:content-css="{ minWidth: '100vw', maxWidth: '100vw', minHeight: '100vh' }"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="show_report = false" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('reports') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div style="position:relative; width:100%; height:0px; padding-bottom:56.25%;">
						<iframe
							style="position:absolute; left:0; top:0; width:100%; height:100%"
							:src="report_url"
							frameborder="0"
						>
						</iframe>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-modal
			v-model="show_report"
			:content-css="{ minWidth: '100vw', maxWidth: '100vw', minHeight: '100vh' }"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="show_report = false" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('reports') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div style="position:relative; width:100%; height:0px; padding-bottom:56.25%;">
						<iframe
							style="position:absolute; left:0; top:0; width:100%; height:100%"
							:src="report_url"
							frameborder="0"
						>
						</iframe>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-dialog v-model="show_report_problem" prevent-close>
			<span slot="title">{{ $t('report_download_failed') }}</span>

			<div slot="message">
				<span class="q-pa-xs">{{ $t('report_download_failed_exceeded_time') }}</span>
				<p class="q-pa-xs">{{ $t('the_report_it_will_be_sent_to_your_email') }}</p>
			</div>

			<template slot="buttons" slot-scope="props" :props="props" class="justify-center">
				<div class="row full-width q-mb-xs">
					<q-btn
						color="positive"
						class="offset-4 col-4"
						:label="$t('ok')"
						@click="show_report_problem = false"
					/>
				</div>
			</template>
		</q-dialog>

		<reports
			v-if="show_report_webcast"
			:show_report_webcast="show_report_webcast"
			:permissions="permissions.reports"
			:id="action_id"
			:title="action_title"
			:webcast_type="webcast_type"
			@hide_report="hideReport()"
		/>
	</q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { date, LocalStorage } from 'quasar'
const { formatDate } = date
import * as moment from 'moment'
import * as tz from 'moment-timezone'
import Filters from 'components/admin/webcasts/Filters.vue'

export default {
	i18nOptions: {
		namespaces: ['custom', 'common', 'webcast', 'admin', 'training', 'courses', 'imports'],
	},

	components: {
		reports: () => import('components/admin/webcasts/Report.vue'),
		filters: Filters,
	},

	data() {
		return {
			server_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: null,
				descending: false,
				sortBy: null,
			},
			loading: false,
			webcasts: [],
			columns: [
				{
					name: 'id',
					required: true,
					label: '#',
					align: 'left',
					field: 'name',
					sortable: true,
					show: true,
				},
				{
					name: 'title',
					required: true,
					label: 'title',
					align: 'left',
					field: 'title',
					sortable: true,
					show: true,
				},
				{
					name: 'code',
					required: true,
					label: 'code',
					align: 'left',
					field: 'code',
					sortable: false,
					show: false,
				},
				{
					name: 'provider',
					required: true,
					label: 'provider',
					align: 'left',
					field: 'provider',
					sortable: 'true',
					show: true,
				},
				{
					name: 'permission',
					required: true,
					label: 'permission',
					align: 'left',
					field: 'permission',
					sortable: false,
					show: false,
				},
				{
					name: 'tags',
					required: true,
					label: 'tags',
					align: 'left',
					field: 'tags',
					sortable: false,
					show: true,
				},
				{
					name: 'total_score',
					required: true,
					label: 'score',
					align: 'left',
					field: 'total_score',
					sortable: false,
					show: false,
				},
				{
					name: 'use_chat',
					required: true,
					label: 'use_chat',
					align: 'left',
					field: 'use_chat',
					sortable: false,
					show: false,
				},
				{
					name: 'chat_moderated',
					required: true,
					label: 'chat_moderated',
					align: 'left',
					field: 'chat_moderated',
					sortable: false,
					show: false,
				},
				{
					name: 'transmission_date',
					required: true,
					label: 'transmission_date',
					align: 'left',
					field: 'transmission_date',
					sortable: true,
					show: true,
				},
				{
					name: 'timezone',
					required: true,
					label: 'timezone',
					align: 'left',
					field: 'timezone',
					sortable: true,
					show: true,
				},
				{
					name: 'created_date',
					required: true,
					label: 'created_date',
					align: 'left',
					field: 'created_date',
					sortable: true,
					show: true,
				},
				{
					name: 'updated_date',
					required: true,
					label: 'updated_date',
					align: 'left',
					field: 'updated_date',
					sortable: true,
					show: true,
				},
				{
					name: 'unpublish_date',
					required: true,
					label: 'unpublish_date',
					align: 'left',
					field: 'unpublish_date',
					sortable: true,
					show: true,
				},
				{
					name: 'status',
					required: true,
					label: 'status',
					align: 'left',
					field: 'status',
					sortable: true,
					show: true,
				},
				{
					name: 'actions',
					required: true,
					label: '',
					align: 'right',
					field: 'edit',
					sortable: false,
					show: true,
				},
			],
			filter: '',
			filter_area: [],
			filter_profile: [],
			filter_status: [],
			filter_tag: [],
			filter_start_date: [],
			filter_end_date: [],
			filter_transmission_start_date: [],
			filter_transmission_end_date: [],
			action_id: '',
			action_title: '',
			webcast_type: '',
			confirm_remove: false,
			show_report: false,
			report_url: '',
			dropdown_reports: false,
			show_report_webcast: false,
			show_report_problem: false,
			show_customize_columns: false,
			permissions: {
				webcasts_manage: false,
				reports_panel: false,
				reports: false,
			},
			show_report: false,
			report_list: [],
			report_url: '',
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			areas: 'registration_lists/country_areas',
			user_groups: 'registration_lists/group_users',
			profiles: 'registration_lists/profiles',
			tags: 'registration_lists/tags',
			clients: 'params/clients',
			id_user_client_selected: 'common/id_user_client_selected',
			user_client_module_items: 'common/user_client_module_items',
			reports: 'registration_lists/reports',
			num_rows: 'datatable/num_rows',
			webcasts_columns: 'datatable_columns/webcasts_columns',
			webcast_types: 'registration_lists/webcast_types',
			reports_store: 'registration_lists/reports',
		}),

		getOptionsFilter() {
			return {
				areas: this.areas ? this.areas : [],
				profiles: this.profiles ? this.profiles : [],
				statuses: [
					{ value: 1, label: this.$t('published') },
					{ value: 2, label: this.$t('unpublished') },
					{ value: 3, label: this.$t('deleted') },
					{ value: 4, label: this.$t('completed') },
				],
				tags: this.tags ? this.tags : [],
			}
		},

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '80vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},

		title_webcasts() {
			if (this.id_user_client_selected) {
				let obj = this.clients.find((obj) => {
					return obj.id === this.id_user_client_selected
				})
				if (obj) {
					return obj.title_webcasts === '' ? 'webinars' : obj.title_webcasts
				}
			}
		},

		filteredReports() {
			let obj = this.reports.filter((obj) => {
				return (
					obj.slug_type === 'webcasts' &&
					obj.url != 'https://www.take5.com.br' &&
					obj.url != ''
				)
			})
			return obj
		},
	},

	methods: {
		request({ pagination }) {
			this.loading = true
			this.server_pagination = pagination

			let order = pagination.descending ? '-' : ''
			let order_by = pagination.sortBy ? order + pagination.sortBy : ''

			let data = {
				page_size: this.server_pagination.rowsPerPage,
				page: this.server_pagination.page,
				search: this.filter,
				area: this.filter_area,
				profile: this.filter_profile,
				status: this.filter_status,
				tag: this.filter_tag,
				start_date: this.filter_start_date,
				end_date: this.filter_end_date,
				transmission_start_date: this.filter_transmission_start_date,
				transmission_end_date: this.filter_transmission_end_date,
				ordering: order_by,
			}

			this.savePagination()

			this.$axios
				.get(
					`/admin_webcasts/webcasts/?${this.$qs.stringify(data, {
						arrayFormat: 'repeat',
					})}`,
					this.getHeader
				)
				.then((response) => {
					this.server_pagination.rowsNumber = response.data.count

					this.webcasts = response.data.results.map((r) => {
						return {
							...r,
							transmission_date: moment(r.transmission_date)
								.tz(r.timezone ? r.timezone : 'Etc/GMT')
								.format('DD/MM/YYYY HH:mm'),
							created_date: moment(r.created_date)
								.tz(r.timezone ? r.timezone : 'Etc/GMT')
								.format('DD/MM/YYYY HH:mm'),
							updated_date: moment(r.updated_date)
								.tz(r.timezone ? r.timezone : 'Etc/GMT')
								.format('DD/MM/YYYY HH:mm'),
							actions: r.id,
							code: r.code,
							provider: r.webcast_type,
							permission: {
								areas: r.areas,
								profiles: r.profiles,
								clients: r.clients,
								user_groups: r.user_groups,
							},
							timezone: `GMT ${moment.tz(new Date(), r.timezone).format('Z')}`,
							unpublish_date: r.unpublish_date
								? moment(r.unpublish_date)
										.tz('Etc/GMT')
										.format('DD/MM/YYYY')
								: '',
						}
					})
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.loading = false
				})
		},

		fetchLists() {
			this.$q.loading.show()

			this.$axios
				.get('/accounts/get_registration_lists/', this.getHeader)
				.then((response) => {
					this.$store.dispatch('registration_lists/SET_LISTS', [response.data])
					this.$q.loading.hide()

					this.request({
						pagination: this.server_pagination,
					})
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		clearAndRequest() {
			this.filter_area = []
			this.filter_profile = []
			this.filter_status = []
			this.filter_tag = []
			this.filter_start_date = []
			this.filter_end_date = []
			this.filter_transmission_start_date = []
			this.filter_transmission_end_date = []

			this.request({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: this.num_rows,
					descending: false,
					sortBy: null,
				},
			})
		},

		requestFiltered({
			area,
			profile,
			status,
			tag,
			start_date,
			end_date,
			transmission_start_date,
			transmission_end_date,
		}) {
			;(this.filter_area = area), (this.filter_profile = profile)
			this.filter_status = status
			this.filter_tag = tag
			this.filter_start_date = start_date
			this.filter_end_date = end_date
			this.filter_transmission_start_date = transmission_start_date
			this.filter_transmission_end_date = transmission_end_date

			this.request({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: this.num_rows,
					descending: false,
					sortBy: null,
				},
			})
		},

		showColumn(list, name) {
			if (list[name]) return list[name].show
			else return false
		},

		saveColumns() {
			this.show_customize_columns = false
			this.saveDatatableColumns()
		},

		confirmRemove(id) {
			this.action_id = id
			this.confirm_remove = true
		},

		removeItem() {
			this.$q.loading.show()

			this.$axios
				.delete(`/admin_webcasts/webcasts/${this.action_id}/`, this.getHeader)
				.then((response) => {
					this.$q.loading.hide()

					this.request({
						pagination: this.server_pagination,
					})

					this.confirm_remove = false

					this.$q.notify({
						message: this.$t('item_removed_success'),
						type: 'positive',
						icon: 'far fa-trash-alt',
					})
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		showWebcastReport(webcast) {
			this.action_id = webcast.id
			this.action_status = webcast.status
			this.action_title = webcast.title
			this.webcast_type = webcast.webcast_type
			this.show_report_webcast = true
		},

		showReport(report) {
			this.report_url = report.url
			this.dropdown_reports = false
			this.show_report = true
		},

		hideReport() {
			this.action_id = ''
			this.action_title = ''
			this.webcast_type = ''
			this.show_report_webcast = false
			this.loadPagination()
		},

		getProvider(provider) {
			if (provider === 1) return 'Youtube'
			else if (provider === 2) return 'Wowza'
			else if (provider === 3) return 'Vimeo'
			else if (provider === 4) return 'Broadcast'
			else if (provider === 5) return this.$t('external_link')
		},

		getStatus(status) {
			if (status == 1) return 'published'
			else if (status == 2) return 'unpublished'
			else if (status == 3) return 'deleted'
			else if (status == 4) return 'ended'
			else if (status == 5) return 'converting'
		},

		getIconStatus(status) {
			if (status == 1) return 'fas fa-check'
			else if (status == 2) return 'fas fa-ban'
			else if (status == 3) return 'fas fa-trash-alt'
			else if (status == 4) return 'fas fa-calendar-check'
			else if (status == 5) return 'fas fa-wrench'
		},

		getColorStatus(status) {
			if (status == 1) return 'positive'
			else if (status == 2) return 'warning'
			else if (status == 3) return 'negative'
			else if (status == 4) return 'info'
			else if (status == 5) return 'info'
		},

		getArea(area) {
			let obj = this.areas.find((obj) => {
				return obj.value === area
			})
			if (obj) return obj.label
			else return ''
		},

		getUserGroup(user_group) {
			let obj = this.user_groups.find((obj) => {
				return obj.value === user_group
			})
			if (obj) return obj.label
			else return ''
		},

		getUserProfile(profile) {
			let obj = this.profiles.find((obj) => {
				return obj.value === profile
			})
			if (obj) return obj.label
			else return ''
		},

		getTag(tag) {
			let obj = this.tags.find((obj) => {
				return obj.value === tag
			})
			if (obj) return obj.label
			else return ''
		},

		getQuantity(permission) {
			return (
				permission.areas.length +
				permission.clients.length +
				permission.user_groups.length +
				permission.profiles.length
			)
		},

		getQuantityTags(tags) {
			return tags.length > 0 ? tags.length : '0'
		},

		savePagination() {
			this.$store.dispatch('datatable/SET_NUM_ROWS', [this.server_pagination.rowsPerPage])
		},

		loadPagination() {
			this.server_pagination.rowsPerPage = this.num_rows
		},

		saveDatatableColumns() {
			this.$store.dispatch('datatable_columns/SET_WEBCASTS_COLUMNS', [this.columns])
		},

		loadDatatableColumns() {
			if (process.env.PROJECT_FOLDER !== 'universidadechevrolet') {
				this.columns = this.columns.filter((c) => c.name !== 'code')

				const storedCol = this.webcasts_columns.find((c) => c.name === 'code')
				if (storedCol) {
					this.$store.dispatch('datatable_columns/SET_WEBCASTS_COLUMNS', [this.columns])
				}
			}
			if (this.$compareStoredColumns(this.columns, this.webcasts_columns))
				this.columns = this.webcasts_columns
			else return
		},

		checkPermissions() {
			const client = this.user_client_module_items.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			if (client) {
				for (const [key, value] of Object.entries(this.permissions)) {
					let obj = client.module_items.find((obj) => obj.slug === key)
					if (obj) {
						this.permissions[key] = obj.permission
					}
				}
			} else return
		},

		downloadReport() {
			if (this.$buttonDelay('webcasts_report_button_delay')) return
			let message = this.$t('generating_import_file_message')
			this.$q.loading.show({
				message,
			})
			this.$axios
				.get(`/admin_webcasts/webcasts/generate_report/`, this.getHeader)
				.then((response) => {
					this.forceFileDownload(response.data.path_url)
				})
				.catch((e) => {
					this.loading = false
					this.show_report_problem = true
					console.log(e)
				})
				.finally(() => {
					this.$store.dispatch('common/SET_WEBCASTS_REPORT_BUTTON_DELAY', [new Date()])
					this.$q.loading.hide()
				})
		},

		forceFileDownload(response) {
			const link = document.createElement('a')
			link.href = response
			document.body.appendChild(link)
			link.click()
		},

		loadReports() {
			if (this.reports_store)
				this.report_list = this.reports_store.filter((obj) => {
					return (
						obj.slug_type === 'webcasts' &&
						obj.url != 'https://www.take5.com.br' &&
						obj.url != ''
					)
				})
		},

		getAuthParamsData(url_original) {
			this.$q.loading.show()
			this.$axios
				.get('/reports/reports/get_looker_studio_challenge_token/', this.getHeader)
				.then((response) => {
					this.report_url = `${url_original}?${response.data.params}`
					this.show_report = true
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		showReport(report) {
			this.$refs.dropdownReport.hide()
			if (report.report_type === 3) {
				this.getAuthParamsData(report.url_original)
			} else {
				this.report_url = report.url
				this.show_report = true
			}
		},
	},

	created() {
		this.loadReports()
		this.loadDatatableColumns()
		this.loadPagination()
		this.fetchLists()
		this.checkPermissions()
	},
}
</script>

<style lang="stylus" scoped>
.filter-card
    border-radius 4px
    box-shadow 0 1px 2px rgba(0,0,0,0.2), 0 1px 1px rgba(0,0,0,0.14), 0 2px 1px -1px rgba(0,0,0,0.12)
</style>
